import { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";

const StyledVideo = styled.video`
  width: 100%;
  max-width: 800px;
  display: block;
  background: #f0f0f0;
`;

export const Video = ({
  src,
  aspectRatio,
}: {
  src: string;
  aspectRatio: number;
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const inViewRef = useRef(false);

  const play = useCallback(() => {
    const videoElement = videoRef.current;
    if (
      !videoElement ||
      !inViewRef.current ||
      document.visibilityState !== "visible" ||
      !videoElement.paused
    )
      return;
    videoElement.defaultMuted = true;
    videoElement.muted = true;
    videoElement.play().catch((error) => {
      if (error.name !== "AbortError") {
        console.warn("video play failed", videoElement.currentSrc, error);
      }
    });
  }, []);

  // Safari decides whether a video may autoplay the moment it starts loading,
  // and React writes muted as a property only after it has written src. With
  // src in the JSX the element is still unmuted when loading begins, so Safari
  // blocks it and overlays a tap-to-play button. Mute first, attach src after.
  const attach = useCallback(
    (videoElement: HTMLVideoElement | null) => {
      videoRef.current = videoElement;
      if (!videoElement) return;
      videoElement.defaultMuted = true;
      videoElement.muted = true;
      videoElement.setAttribute("muted", "");
      videoElement.playsInline = true;
      videoElement.autoplay = true;
      if (videoElement.getAttribute("src") !== src) videoElement.src = src;
    },
    [src],
  );

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    // Safari may suspend playback while a video is offscreen. Retry when it
    // actually enters the viewport, including after a page or tab is restored.
    const resume = () => {
      const bounds = videoElement.getBoundingClientRect();
      inViewRef.current =
        bounds.width > 0 &&
        bounds.height > 0 &&
        bounds.bottom > 0 &&
        bounds.top < window.innerHeight &&
        bounds.right > 0 &&
        bounds.left < window.innerWidth;
      if (inViewRef.current && document.visibilityState === "visible") play();
      else videoElement.pause();
    };
    const observer = new IntersectionObserver(resume, { threshold: 0 });
    observer.observe(videoElement);

    resume();
    videoElement.addEventListener("loadedmetadata", play);
    videoElement.addEventListener("canplay", play);
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("pageshow", resume);
    // These are user-activation events in Safari; scrolling is not one.
    // This fallback never replaces the automatic attempt on entering view.
    window.addEventListener("touchend", resume, { passive: true });
    window.addEventListener("click", resume);
    window.addEventListener("keydown", resume);

    return () => {
      observer.disconnect();
      inViewRef.current = false;
      videoElement.removeEventListener("loadedmetadata", play);
      videoElement.removeEventListener("canplay", play);
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("touchend", resume);
      window.removeEventListener("click", resume);
      window.removeEventListener("keydown", resume);
      videoElement.pause();
    };
  }, [play, src]);

  return (
    <StyledVideo
      ref={attach}
      // Reserve the original proportions before the first frame decodes.
      style={{ aspectRatio }}
      autoPlay
      loop
      muted
      playsInline
      preload="metadata"
    />
  );
};
